import { useEffect, useRef, useState } from 'react';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter } from './ui/alert-dialog';
import { Button } from './ui/button';
import { clearTabToken, hasTabSession } from '../lib/tabSession';

const API = process.env.REACT_APP_BACKEND_URL;
const CHANNEL = 'necs-idle-session';

export const IdleSessionGuard = ({ user, onExpired }) => {
  const [remaining, setRemaining] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const controls = useRef({});
  const expireCallback = useRef(onExpired);
  expireCallback.current = onExpired;

  useEffect(() => {
    if (!user) return;
    let stopped = false, ending = false, extending = false, state = null, offset = 0, lastSend = 0;
    let trailing = null;
    const isolated = hasTabSession();
    const announce = type => {
      if (!isolated && state) localStorage.setItem(CHANNEL, JSON.stringify({ type, key: state.session_key, at: Date.now() }));
    };
    const end = async (broadcast = true, checkDeadline = false) => {
      if (ending || stopped) return;
      ending = true;
      clearTimeout(trailing);
      if (checkDeadline) {
        try {
          const response = await fetch(`${API}/api/auth/session/status`, { credentials: 'include', cache: 'no-store', signal: AbortSignal.timeout(5000) });
          if (response.ok) {
            const current = await response.json();
            if (Date.parse(current.expires_at) > Date.parse(current.server_now)) {
              ending = false; accept(current); return;
            }
          }
        } catch { /* Known server deadline still applies while offline. */ }
      }
      if (broadcast) announce('logout');
      // Idle expiry never restores an impersonator's administrator session.
      if (state) try {
        const r = await fetch(`${API}/api/auth/session/revoke`, { method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ session_key: state.session_key }), signal: AbortSignal.timeout(5000) });
        if (r.status === 409) { ending = false; await sync(); return; }
      } catch { /* Server deadlines still reject access when offline. */ }
      if (stopped) return;
      if (isolated) clearTabToken();
      setRemaining(null);
      expireCallback.current();
    };
    const accept = data => {
      if (stopped || ending) return;
      state = data;
      offset = Date.parse(data.server_now) - Date.now();
      tick();
    };
    const sync = async () => {
      try {
        const r = await fetch(`${API}/api/auth/session/status`, { credentials: 'include', cache: 'no-store' });
        if (r.ok) accept(await r.json());
        else if ([401, 403].includes(r.status)) await end();
      } catch { /* Keep the known deadline; no extension on reconnect. */ }
    };
    const extend = async () => {
      if (!state || extending || ending || stopped) return;
      if (Date.now() + offset >= Date.parse(state.expires_at)) return end(true, true);
      extending = true; setBusy(true); setError(''); lastSend = Date.now();
      try {
        const r = await fetch(`${API}/api/auth/session/extend`, { method: 'POST', credentials: 'include' });
        if (r.ok) { accept(await r.json()); announce('extended'); }
        else if ([401, 403].includes(r.status)) await end();
        else throw new Error();
      } catch { if (!stopped) setError('Unable to extend your session. Please try again.'); }
      finally { extending = false; if (!stopped) setBusy(false); }
    };
    const tick = () => {
      if (!state || stopped || ending) return;
      const now = Date.now() + offset;
      const left = Math.max(0, Math.ceil((Date.parse(state.expires_at) - now) / 1000));
      if (!left) { void end(true, true); return; }
      setRemaining(now >= Date.parse(state.warn_at) ? left : null);
    };
    const activity = event => {
      if (!event.isTrusted || document.visibilityState !== 'visible' || !state || ending) return;
      // Once the warning is shown, only the explicit Extend button renews.
      if (Date.now() + offset >= Date.parse(state.warn_at)) { tick(); return; }
      clearTimeout(trailing);
      if (Date.now() - lastSend >= 10000) void extend();
      else trailing = setTimeout(() => void extend(), Math.max(500, 10000 - (Date.now() - lastSend)));
    };
    const visible = () => { if (document.visibilityState === 'visible') { tick(); void sync(); } };
    const storage = event => {
      if (isolated || event.key !== CHANNEL || !event.newValue || !state) return;
      try {
        const data = JSON.parse(event.newValue);
        if (data.key !== state.session_key) return;
        if (data.type === 'logout') void end(false);
        else void sync();
      } catch { /* Ignore unrelated storage payloads. */ }
    };
    controls.current = { extend, end };
    const events = ['pointerdown', 'pointermove', 'keydown', 'touchstart', 'wheel'];
    events.forEach(name => window.addEventListener(name, activity, { passive: true, capture: true }));
    document.addEventListener('visibilitychange', visible);
    window.addEventListener('pageshow', visible);
    window.addEventListener('storage', storage);
    const timer = setInterval(tick, 1000);
    const statusTimer = setInterval(() => { if (!ending && document.visibilityState === 'visible') void sync(); }, 30000);
    void sync();
    return () => {
      stopped = true; clearInterval(timer); clearInterval(statusTimer); clearTimeout(trailing);
      events.forEach(name => window.removeEventListener(name, activity, true));
      document.removeEventListener('visibilitychange', visible); window.removeEventListener('pageshow', visible); window.removeEventListener('storage', storage);
    };
  }, [user?.user_id]);

  return <AlertDialog open={remaining !== null && !!user}>
    <AlertDialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md bg-zinc-950 text-white border-[#C9A962] z-[200]" data-testid="session-timeout-warning">
      <AlertDialogHeader>
        <AlertDialogTitle data-testid="session-timeout-title">Your session is about to end</AlertDialogTitle>
        <AlertDialogDescription className="text-zinc-300" data-testid="session-timeout-description">You’ve been inactive for 13 minutes. Extend your session to stay signed in.</AlertDialogDescription>
      </AlertDialogHeader>
      <p className="text-lg text-[#C9A962]" role="timer" data-testid="session-timeout-countdown">Signing out in {Math.floor((remaining || 0) / 60)}:{String((remaining || 0) % 60).padStart(2, '0')}</p>
      {error && <p role="alert" data-testid="session-timeout-error">{error}</p>}
      <AlertDialogFooter>
        <Button variant="outline" onClick={() => controls.current.end?.()} data-testid="session-timeout-logout">Log out</Button>
        <Button disabled={busy} onClick={() => controls.current.extend?.()} className="bg-[#C9A962] text-black hover:bg-amber-300" data-testid="session-timeout-extend">{busy ? 'Extending…' : 'Extend session'}</Button>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>;
};